import { useState, useEffect, useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate, useSearchParams } from 'react-router'
import {
  ArrowLeft,
  ArrowRightLeft,
  AlertTriangle,
  Building2,
  Users,
  Package,
  FileCheck,
  Shield,
  ShieldCheck,
  Activity,
  ChevronRight,
  Mail,
  Handshake,
  Network,
  Store,
  Pencil,
  Save,
  Trash2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import api, { setAuthToken, clearActiveOrgContext } from '@/admin/lib/api'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { StaffApiKeysSection } from '@/admin/pages/staff/staff-api-keys-section'
import { StaffThemeTab } from '@/admin/pages/staff/staff-tenant-branding'
import { Button } from '@/shared/components/ui/button'
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/shared/components/ui/tabs'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { SearchInput } from '@/shared/components/ui/search-input'
import { Label } from '@/shared/components/ui/label'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import { cn } from '@/shared/lib/utils'
import { useFormatters } from '@/shared/lib/formatting'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import { toastApiError } from '@/shared/lib/api-error'
interface TenantDetail {
  tenant: {
    uuid: string
    name: string
    slug: string
    subscription_plan: string
    subscription_status: string
    is_active: boolean
    onboarding_completed: boolean
    created_at: string
    feature_flags: Record<string, boolean>
    settings: Record<string, unknown>
  }
  totals: {
    organisations: number
    users: number
    products: number
    registrations: number
    warranties: number
    emails_sent: number
  }
  organisations: Array<{
    uuid: string
    name: string
    slug: string
    user_count: number
    product_count: number
    registration_count: number
  }>
  partnership_count: number
  partner_network: {
    total_partnerships: number
    main_groups: number
    dealer_groups: number
    independent_partners: number
    dealer_sites: number
  }
  recent_members: Array<{
    user__uuid: string
    user__email: string
    user__first_name: string
    user__last_name: string
    user__is_staff: boolean
    role__name: string | null
    created_at: string
  }>
  recent_activity: Array<{
    id: string
    action: string
    module: string
    description: string
    timestamp: string
    user__email: string | null
    impersonator__email: string | null
  }>
}

interface FeatureFlagDefinition {
  key: string
  label: string
  description: string
  category: string
  default_enabled: boolean
  deprecated: boolean
  internal?: boolean
}

const CATEGORY_LABELS: Record<string, string> = {
  product: 'Product',
  warranty: 'Warranty',
  registration: 'Registration',
  transfer: 'Transfer',
  integration: 'Integration',
}

function categoryLabel(cat: string): string {
  return CATEGORY_LABELS[cat] ?? cat.charAt(0).toUpperCase() + cat.slice(1)
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(iso: string, formatDate: (iso: string) => string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return formatDate(iso)
}

function actionColor(action: string): string {
  switch (action) {
    case 'provision_organisation':
      return 'bg-[var(--green-bg)] text-[var(--green)]'
    case 'impersonate':
      return 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]'
    case 'delete':
      return 'bg-[var(--status-error-bg)] text-[var(--status-error)]'
    default:
      return 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
  }
}

function planLabel(plan: string): string {
  const map: Record<string, string> = {
    trial: 'Trial',
    starter: 'Starter',
    growth: 'Growth',
    scale: 'Scale',
    enterprise: 'Enterprise',
  }
  return map[plan] ?? plan
}

// ---------------------------------------------------------------------------
// Stat card (matches dashboard)
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: number | string
  icon: React.ComponentType<{ className?: string }>
}) {
  return (
    <div
      className="flex items-start gap-4 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-5"
    >
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)]"
      >
        <Icon className="h-4 w-4 text-[var(--green)]" />
      </div>
      <div>
        <p className="mb-1 text-[24px] leading-none text-[var(--ink)]">
          {value}
        </p>
        <p className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          {label}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

type OrgRow = TenantDetail['organisations'][number]

const orgColumns: DataTableColumn<OrgRow>[] = [
  {
    id: 'name',
    header: 'Organisation',
    cell: (org) => (
      <div className="flex items-center gap-2.5">
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] text-[10px] font-medium text-[var(--green)]"
        >
          {org.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="leading-tight text-[var(--ink)]">
            {org.name}
          </p>
          <p className="text-[10px] font-mono text-[var(--ink-3)]">
            {org.slug}
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'user_count',
    header: 'Users',
    cell: (org) => <div className="text-right">{org.user_count}</div>,
  },
  {
    id: 'product_count',
    header: 'Products',
    cell: (org) => <div className="text-right">{org.product_count}</div>,
  },
  {
    id: 'registration_count',
    header: 'Registrations',
    cell: (org) => <div className="text-right">{org.registration_count}</div>,
  },
]

interface PartnerRow {
    uuid: string
    'downstream_organisation__uuid': string
    'downstream_organisation__name': string
    'downstream_organisation__parent_organisation__name': string | null
    partner_type: 'group' | 'site' | 'independent'
    'role__name': string | null
    is_active: boolean
    user_access_count: number
    created_at: string
  }

function getPartnerColumns(formatDate: (iso: string) => string): DataTableColumn<PartnerRow>[] {
  return [
  {
    id: 'partner',
    header: 'Partner',
    cell: (p) => (
      <div className="flex items-center gap-2.5">
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-3)]"
        >
          <Building2 className="h-3.5 w-3.5" />
        </div>
        <p className="leading-tight text-[var(--ink)]">
          {p['downstream_organisation__name']}
        </p>
      </div>
    ),
  },
  {
    id: 'role',
    header: 'Role',
    cell: (p) => (
      <span className="text-[var(--ink-3)]">
        {p['role__name'] ?? '-'}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (p) => (
      <span
        className={cn(
          'rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.08em]',
          p.is_active
            ? 'bg-[var(--green-bg)] text-[var(--green)]'
            : 'bg-[var(--status-error-bg)] text-[var(--status-error)]'
        )}
      >
        {p.is_active ? 'Active' : 'Suspended'}
      </span>
    ),
  },
  {
    id: 'user_access_count',
    header: 'Users',
    cell: (p) => <div className="text-right">{p.user_access_count}</div>,
  },
  {
    id: 'added',
    header: 'Added',
    cell: (p) => (
      <div className="text-right text-[var(--ink-3)]">
        {formatDate(p.created_at)}
      </div>
    ),
  },
  ]
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const DETAIL_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'theme', label: 'Theme' },
] as const

type DetailTabValue = (typeof DETAIL_TABS)[number]['value']

export function StaffTenantDetailPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const { formatDate } = useFormatters()
  const partnerColumns = useMemo(() => getPartnerColumns(formatDate), [formatDate])
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTab = searchParams.get('tab')
  const activeTab: DetailTabValue = DETAIL_TABS.some((t) => t.value === requestedTab)
    ? (requestedTab as DetailTabValue)
    : 'overview'
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'error' } | { status: 'loaded'; data: TenantDetail }
  >({ status: 'loading' })

  useEffect(() => {
    if (!uuid) return
    let cancelled = false
    api
      .get(`/staff/tenants/${uuid}/`)
      .then((res) => { if (!cancelled) setState({ status: 'loaded', data: res.data }) })
      .catch(() => { if (!cancelled) setState({ status: 'error' }) })
    return () => { cancelled = true }
  }, [uuid])

  // Paginated partnerships
  const [partnerSearch, setPartnerSearch] = useState('')
  const [partnerPage, setPartnerPage] = useState(1)
  const [partners, setPartners] = useState<PartnerRow[]>([])
  const [partnerTotal, setPartnerTotal] = useState(0)
  const [partnersLoading, setPartnersLoading] = useState(false)
  const partnerPageSize = 15

  const fetchPartners = useCallback(async () => {
    if (!uuid) return
    setPartnersLoading(true)
    try {
      const params: Record<string, string> = {
        page: String(partnerPage),
        page_size: String(partnerPageSize),
      }
      if (partnerSearch.length >= 2) params.search = partnerSearch
      const res = await api.get(`/staff/tenants/${uuid}/partnerships/`, { params })
      setPartners(res.data.results)
      setPartnerTotal(res.data.count)
    } catch { /* silent */ } finally {
      setPartnersLoading(false)
    }
  }, [uuid, partnerPage, partnerSearch])

  useEffect(() => { fetchPartners() }, [fetchPartners])

  // Reset to page 1 when search changes
  useEffect(() => { setPartnerPage(1) }, [partnerSearch])

  // Paginated tenant members (searchable by name, email, organisation)
  type MemberRow = {
    user_uuid: string
    user_email: string
    user_first_name: string
    user_last_name: string
    user_is_staff: boolean
    organisation_uuid: string
    organisation_name: string
    role_name: string | null
    role_names?: string[]
    is_partner: boolean
    created_at: string
  }
  type MemberScope = 'all' | 'tenant' | 'partner'
  const [memberSearch, setMemberSearch] = useState('')
  const [memberScope, setMemberScope] = useState<MemberScope>('all')
  const [memberPage, setMemberPage] = useState(1)
  const [members, setMembers] = useState<MemberRow[]>([])
  const [memberTotal, setMemberTotal] = useState(0)
  const [membersLoading, setMembersLoading] = useState(false)
  const memberPageSize = 10

  const fetchMembers = useCallback(async () => {
    if (!uuid) return
    setMembersLoading(true)
    try {
      const params: Record<string, string> = {
        page: String(memberPage),
        page_size: String(memberPageSize),
      }
      if (memberSearch.length >= 2) params.search = memberSearch
      if (memberScope !== 'all') params.scope = memberScope
      const res = await api.get(`/staff/tenants/${uuid}/members/`, { params })
      setMembers(res.data.results)
      setMemberTotal(res.data.count)
    } catch { /* silent */ } finally {
      setMembersLoading(false)
    }
  }, [uuid, memberPage, memberSearch, memberScope])

  useEffect(() => { fetchMembers() }, [fetchMembers])
  useEffect(() => { setMemberPage(1) }, [memberSearch, memberScope])

  // Recent activity collapse/expand
  const [activityExpanded, setActivityExpanded] = useState(false)

  // Feature flags management
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({})
  const [flagsSaving, setFlagsSaving] = useState(false)

  useEffect(() => {
    if (state.status === 'loaded') {
      setFeatureFlags(state.data.tenant.feature_flags ?? {})
    }
  }, [state])

  // Feature flag definitions (dynamic from registry)
  const [flagDefinitions, setFlagDefinitions] = useState<FeatureFlagDefinition[]>([])
  const [flagDefsLoading, setFlagDefsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    api
      .get('/staff/feature-flag-definitions/')
      .then((res) => {
        if (!cancelled) setFlagDefinitions(res.data)
      })
      .catch((err) => {
        if (!cancelled) toastApiError(err, 'Failed to load feature flag definitions')
      })
      .finally(() => {
        if (!cancelled) setFlagDefsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Bulk-import per-claim-type toggles
  type ClaimImportType = {
    uuid: string
    name: string
    key: string
    bulk_import_enabled: boolean
  }
  const queryClient = useQueryClient()
  const claimImportTypesKey = ['staff', 'tenant', uuid, 'claim-import-types']

  const {
    data: claimImportTypes = [],
    isLoading: claimImportLoading,
    error: claimImportError,
  } = useQuery({
    queryKey: claimImportTypesKey,
    enabled: !!uuid,
    queryFn: async () => {
      const res = await api.get(`/staff/tenants/${uuid}/claim-import-types/`)
      return (res.data.claim_types ?? []) as ClaimImportType[]
    },
  })

  useEffect(() => {
    if (claimImportError) toastApiError(claimImportError, 'Failed to load claim types')
  }, [claimImportError])

  const { groupedFlags, legacyFlags } = useMemo(() => {
    const groups: Record<string, FeatureFlagDefinition[]> = {}
    const definedKeys = new Set<string>()
    for (const def of flagDefinitions) {
      definedKeys.add(def.key)
      // Internal flags gate code but are activated by a tenant's config/seed,
      // not by an operator toggle - keep them out of the managed UI. They stay
      // in definedKeys so they are also suppressed from the legacy list below.
      if (def.internal) continue
      // bulk_imports is managed in the dedicated Bulk Import section below.
      if (def.key === 'bulk_imports') continue
      if (!groups[def.category]) groups[def.category] = []
      groups[def.category].push(def)
    }
    const legacy: Array<{ key: string; label: string; description: string }> = []
    for (const key of Object.keys(featureFlags)) {
      if (!definedKeys.has(key)) {
        legacy.push({
          key,
          label: key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase()),
          description: 'Legacy flag - not in the definitions registry.',
        })
      }
    }
    return { groupedFlags: groups, legacyFlags: legacy }
  }, [flagDefinitions, featureFlags])

  // SSO provider management
  interface SSOProvider {
    uuid: string
    display_name: string
    entity_id: string
    sso_url: string
    slo_url: string
    sp_entity_id: string
    x509_certificate: string
    is_enabled: boolean
  }
  const [ssoProvider, setSsoProvider] = useState<SSOProvider | null>(null)
  const [ssoLoading, setSsoLoading] = useState(true)
  const [ssoEditing, setSsoEditing] = useState(false)
  const [ssoForm, setSsoForm] = useState({
    display_name: '',
    entity_id: '',
    sso_url: '',
    slo_url: '',
    sp_entity_id: '',
    x509_certificate: '',
    is_enabled: true,
  })
  const [ssoSaving, setSsoSaving] = useState(false)
  const [ssoDeleting, setSsoDeleting] = useState(false)
  const [ssoConfirmOpen, setSsoConfirmOpen] = useState(false)

  // Danger zone - bulk-delete registrations
  interface BulkDeletePreview {
    holdings: number
    registration_field_values: number
    warranties: number
    claims: number
    inspections: number
    transfer_registrations: number
    transfers: number
  }
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkDeletePreview, setBulkDeletePreview] =
    useState<BulkDeletePreview | null>(null)
  const [bulkDeleteIsProd, setBulkDeleteIsProd] = useState(false)
  const [bulkDeleteProdPhrase, setBulkDeleteProdPhrase] = useState<string | null>(
    null,
  )
  const [bulkDeletePreviewLoading, setBulkDeletePreviewLoading] = useState(false)
  const [bulkDeleteApplying, setBulkDeleteApplying] = useState(false)
  const [bulkDeleteConfirmInput, setBulkDeleteConfirmInput] = useState('')
  const [bulkDeleteProdInput, setBulkDeleteProdInput] = useState('')
  const [bulkDeleteAlsoTransfers, setBulkDeleteAlsoTransfers] = useState(false)

  const fetchSSO = useCallback(async () => {
    if (state.status !== 'loaded') return
    setSsoLoading(true)
    try {
      const res = await api.get('/auth/sso/providers/', {
        params: { tenant_slug: state.data.tenant.slug },
      })
      const providers = res.data || []
      if (providers.length > 0) {
        // Fetch detail to get certificate
        const detail = await api.get(`/auth/sso/providers/${providers[0].uuid}/`)
        setSsoProvider(detail.data)
        setSsoForm({
          display_name: detail.data.display_name || '',
          entity_id: detail.data.entity_id || '',
          sso_url: detail.data.sso_url || '',
          slo_url: detail.data.slo_url || '',
          sp_entity_id: detail.data.sp_entity_id || '',
          x509_certificate: detail.data.x509_certificate || '',
          is_enabled: detail.data.is_enabled ?? true,
        })
      }
    } catch { /* no SSO configured */ }
    finally { setSsoLoading(false) }
  }, [state])

  useEffect(() => { fetchSSO() }, [fetchSSO])

  async function handleSSOSave() {
    if (!uuid) return
    setSsoSaving(true)
    try {
      if (ssoProvider) {
        await api.patch(`/auth/sso/providers/${ssoProvider.uuid}/`, ssoForm)
        toast.success('SSO provider updated')
      } else {
        await api.post('/auth/sso/providers/', {
          ...ssoForm,
          tenant_uuid: state.status === 'loaded' ? state.data.tenant.uuid : '',
        })
        toast.success('SSO provider created')
      }
      setSsoEditing(false)
      await fetchSSO()
    } catch (err: unknown) {
      toastApiError(err, 'Failed to save SSO provider');
    } finally { setSsoSaving(false) }
  }

  async function handleSSODelete() {
    if (!ssoProvider) return
    setSsoDeleting(true)
    try {
      await api.delete(`/auth/sso/providers/${ssoProvider.uuid}/`)
      toast.success('SSO provider removed')
      setSsoProvider(null)
      setSsoEditing(false)
      setSsoForm({ display_name: '', entity_id: '', sso_url: '', slo_url: '', sp_entity_id: '', x509_certificate: '', is_enabled: true })
      setSsoConfirmOpen(false)
    } catch (err) {
      toastApiError(err, 'Failed to delete SSO provider')
    } finally {
      setSsoDeleting(false)
    }
  }

  const openBulkDeleteDialog = useCallback(async () => {
    if (!uuid) return
    setBulkDeleteOpen(true)
    setBulkDeleteConfirmInput('')
    setBulkDeleteProdInput('')
    setBulkDeleteAlsoTransfers(false)
    setBulkDeletePreview(null)
    setBulkDeletePreviewLoading(true)
    try {
      const res = await api.post(
        `/staff/tenants/${uuid}/registrations/bulk-delete/`,
        { apply: false },
      )
      setBulkDeletePreview(res.data.preview)
      setBulkDeleteIsProd(Boolean(res.data.is_production))
      setBulkDeleteProdPhrase(res.data.prod_confirmation_phrase ?? null)
    } catch (err) {
      toastApiError(err, 'Failed to load delete preview')
      setBulkDeleteOpen(false)
    } finally {
      setBulkDeletePreviewLoading(false)
    }
  }, [uuid])

  async function handleBulkDeleteApply() {
    if (!uuid || state.status !== 'loaded') return
    setBulkDeleteApplying(true)
    try {
      const res = await api.post(
        `/staff/tenants/${uuid}/registrations/bulk-delete/`,
        {
          apply: true,
          delete_transfers: bulkDeleteAlsoTransfers,
          confirm_phrase: bulkDeleteConfirmInput,
          confirm_prod_phrase: bulkDeleteProdInput,
        },
      )
      const deletedTotal = res.data?.deleted_total ?? 0
      toast.success(
        `Deleted ${deletedTotal.toLocaleString()} row(s) for ${state.data.tenant.slug}.`,
      )
      setBulkDeleteOpen(false)
      // Refresh tenant detail so the registration totals reflect the wipe.
      try {
        const refreshed = await api.get(`/staff/tenants/${uuid}/`)
        setState({ status: 'loaded', data: refreshed.data })
      } catch { /* non-fatal - counts may show stale until reload */ }
    } catch (err) {
      toastApiError(err, 'Failed to delete registrations')
    } finally {
      setBulkDeleteApplying(false)
    }
  }

  const toggleFeatureFlag = useCallback(async (flag: string, enabled: boolean) => {
    if (!uuid) return
    const prev = { ...featureFlags }
    const updated = { ...featureFlags, [flag]: enabled }
    setFeatureFlags(updated)
    setFlagsSaving(true)
    try {
      await api.patch(`/staff/tenants/${uuid}/`, { feature_flags: updated })
      toast.success(`Feature flag "${flag.replace(/_/g, ' ')}" ${enabled ? 'enabled' : 'disabled'}`)
    } catch (err) {
      setFeatureFlags(prev)
      toastApiError(err, 'Failed to update feature flag');
    } finally {
      setFlagsSaving(false)
    }
  }, [uuid, featureFlags])

  const claimImportMutation = useMutation({
    mutationFn: async ({
      schemaUuid,
      enabled,
    }: {
      schemaUuid: string
      enabled: boolean
    }) => {
      await api.patch(`/staff/tenants/${uuid}/claim-import-types/`, {
        schema_uuid: schemaUuid,
        enabled,
      })
    },
    onMutate: async ({ schemaUuid, enabled }) => {
      await queryClient.cancelQueries({ queryKey: claimImportTypesKey })
      const prev = queryClient.getQueryData<ClaimImportType[]>(claimImportTypesKey)
      queryClient.setQueryData<ClaimImportType[]>(claimImportTypesKey, (list = []) =>
        list.map((t) =>
          t.uuid === schemaUuid ? { ...t, bulk_import_enabled: enabled } : t,
        ),
      )
      return { prev }
    },
    onSuccess: (_data, { enabled }) => {
      toast.success(`Bulk import ${enabled ? 'enabled' : 'disabled'}`)
    },
    onError: (err, _vars, context) => {
      if (context?.prev) queryClient.setQueryData(claimImportTypesKey, context.prev)
      toastApiError(err, 'Failed to update bulk import')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: claimImportTypesKey })
    },
  })

  const toggleClaimImport = useCallback(
    (schemaUuid: string, enabled: boolean) => {
      if (!uuid) return
      claimImportMutation.mutate({ schemaUuid, enabled })
    },
    [uuid, claimImportMutation],
  )
  const claimImportSavingUuid = claimImportMutation.isPending
    ? claimImportMutation.variables?.schemaUuid
    : null

  const bulkImportsOn = Boolean(featureFlags['bulk_imports'])

  const handleImpersonate = useCallback(async (userUuid: string) => {
    try {
      const res = await api.post(`/staff/impersonate/${userUuid}/`)
      if (res.data.session_token) setAuthToken(res.data.session_token)
      // The tab still holds the staff user's active org selection, which would
      // ride along as X-Active-User-Organisation-UUID and strand the
      // impersonated user with no partner context.
      clearActiveOrgContext()
      toast.success(`Now impersonating ${res.data.user.email}`)
      globalThis.location.assign('/dashboard')
    } catch (err: unknown) {
      toastApiError(err, 'Failed to impersonate');
    }
  }, [])

  const memberColumns: DataTableColumn<MemberRow>[] = useMemo(() => [
    {
      id: 'user',
      header: 'User',
      cell: (m) => (
        <div className="min-w-0">
          <p className="truncate leading-tight text-[var(--ink)]">
            {m.user_first_name || m.user_last_name
              ? `${m.user_first_name} ${m.user_last_name}`.trim()
              : m.user_email}
          </p>
          <p className="truncate text-[10px] text-[var(--ink-3)]">
            {m.user_email}
          </p>
        </div>
      ),
    },
    {
      id: 'organisation',
      header: 'Organisation',
      cell: (m) => (
        <span className="text-[var(--ink-3)]">{m.organisation_name}</span>
      ),
    },
    {
      id: 'role',
      header: 'Role',
      cell: (m) => {
        const roles = m.role_names?.length
          ? m.role_names
          : m.role_name
            ? [m.role_name]
            : []
        if (roles.length === 0)
          return <span className="text-[var(--ink-3)]">-</span>
        return (
          <div className="flex flex-wrap gap-1">
            {roles.map((name) => (
              <span
                key={name}
                className="rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] uppercase tracking-[0.08em] text-[var(--ink-3)]"
              >
                {name}
              </span>
            ))}
          </div>
        )
      },
    },
    {
      id: 'type',
      header: 'Type',
      cell: (m) => (
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.08em]',
            m.is_partner
              ? 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
              : 'bg-[var(--green-bg)] text-[var(--green)]',
          )}
        >
          {m.is_partner ? 'Partner' : "Tenant"}
        </span>
      ),
    },
    {
      id: 'added',
      header: 'Added',
      cell: (m) => (
        <span className="text-[var(--ink-3)]">
          {formatDate(m.created_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: (m) => (
        !m.user_is_staff ? (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="icon"
              title={`Impersonate ${m.user_email}`}
              onClick={(e) => { e.stopPropagation(); handleImpersonate(m.user_uuid) }}
              className="h-6 w-6 rounded-md cursor-pointer text-[var(--ink-3)] hover:bg-[var(--status-warn-bg)] hover:text-[var(--status-warn)]"
            >
              <ArrowRightLeft className="h-3 w-3" />
            </Button>
          </div>
        ) : null
      ),
    },
  ], [handleImpersonate, formatDate])

  if (state.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg)]">
        <p className="text-[13px] text-[var(--ink-3)]">Loading…</p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[var(--bg)]">
        <p className="text-[13px] text-[var(--ink-3)]">Tenant not found</p>
        <Button
          variant="link"
          onClick={() => navigate('/staff/dashboard')}
          className="cursor-pointer text-[11px] text-[var(--green)] underline"
        >
          Back to dashboard
        </Button>
      </div>
    );
  }

  const { tenant, totals, organisations, partnership_count, partner_network, recent_activity } = state.data

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      {/* Header bar */}
      <header
        className="flex h-14 items-center gap-4 border-b-[0.5px] border-[var(--rule)] px-6"
      >
        <Button
          variant="ghost"
          onClick={() => navigate('/staff/dashboard')}
          className="flex cursor-pointer items-center gap-1.5 rounded-[4px] px-2 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-colors hover:bg-[var(--bg-subtle)]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Dashboard
        </Button>
        <div className="h-4 w-px bg-[var(--rule)]" />
        <span className="clara-section-label tracking-[0.14em]">
          iWarranty
        </span>
        <span
          className="rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--green)]"
        >
          Staff
        </span>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Title */}
        <div className="flex items-start justify-between mb-10">
          <div>
            <h1 className="clara-title mb-1">{tenant.name}</h1>
            <div className="flex items-center gap-3">
              <span className="text-[12px] font-mono text-[var(--ink-3)]">
                {tenant.slug}
              </span>
              <span
                className={cn(
                  'rounded-full border-[0.5px] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em]',
                  tenant.is_active
                    ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]'
                    : 'border-[var(--status-error-border)] bg-[var(--status-error-bg)] text-[var(--status-error)]'
                )}
              >
                {tenant.is_active ? 'Active' : 'Inactive'}
              </span>
              <span
                className="rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
              >
                {planLabel(tenant.subscription_plan)}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <p className="text-[11px] text-[var(--ink-3)]">
              Created {formatDate(tenant.created_at)}
            </p>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(value) =>
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev)
                next.set('tab', value)
                return next
              },
              { replace: true },
            )
          }
        >
          <TabsList variant="line" className="mb-8">
            {DETAIL_TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="theme">
            <StaffThemeTab uuid={uuid ?? ''} />
          </TabsContent>

          <TabsContent value="overview">
        {/* ── Stats Grid ──────────────────────────────────── */}
        <section className="mb-10">
          <h3
            className="clara-section-label mb-5 border-b-[0.5px] border-[var(--rule)] pb-3"
          >
            Overview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard label="Organisations" value={totals.organisations} icon={Building2} />
            <StatCard label="Users" value={totals.users} icon={Users} />
            <StatCard label="Products" value={totals.products} icon={Package} />
            <StatCard label="Registrations" value={totals.registrations} icon={FileCheck} />
            <StatCard label="Warranties" value={totals.warranties} icon={Shield} />
          </div>
          <div className="flex gap-4 mt-4">
            <div className="flex items-center gap-3 rounded-xl bg-[var(--bg-subtle)] px-4 py-3">
              <Mail className="h-3.5 w-3.5 text-[var(--ink-3)]" />
              <span className="text-[13px] text-[var(--ink)]">
                {totals.emails_sent}
              </span>
              <span className="text-[11px] text-[var(--ink-3)]">emails sent</span>
            </div>
          </div>
        </section>

        {/* ── Feature Flags ─────────────────────────────────── */}
        <section className="mb-10">
          <h3 className="clara-section-label mb-5 border-b-[0.5px] border-[var(--rule)] pb-3">
            Feature Flags
          </h3>

          {flagDefsLoading ? (
            <p className="py-4 text-[12px] text-[var(--ink-3)]">
              Loading flag definitions…
            </p>
          ) : (
            <>
              {Object.entries(groupedFlags).map(([category, defs]) => (
                <div key={category} className="mb-6">
                  <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    {categoryLabel(category)}
                  </p>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {defs.map((flag) => (
                      <div
                        key={flag.key}
                        className="flex items-start justify-between gap-3 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-4 py-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-[12px] text-[var(--ink)]">
                              {flag.label}
                            </p>
                            {flag.deprecated && (
                              <Badge variant="outline" className="text-[9px] text-[var(--status-warn)]">
                                Deprecated
                              </Badge>
                            )}
                          </div>
                          <p className="mt-0.5 text-[11px] leading-snug text-[var(--ink-3)] line-clamp-2">
                            {flag.description}
                          </p>
                        </div>
                        <Switch
                          checked={Boolean(featureFlags[flag.key])}
                          disabled={flagsSaving}
                          aria-label={`Toggle ${flag.label}`}
                          onCheckedChange={(checked) => toggleFeatureFlag(flag.key, checked)}
                          className="mt-0.5 shrink-0"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {legacyFlags.length > 0 && (
                <div className="mb-6">
                  <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    Legacy
                  </p>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {legacyFlags.map((flag) => (
                      <div
                        key={flag.key}
                        className="flex items-start justify-between gap-3 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-4 py-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-[12px] text-[var(--ink)]">
                              {flag.label}
                            </p>
                            <Badge variant="outline" className="text-[9px] text-[var(--ink-3)]">
                              Unregistered
                            </Badge>
                          </div>
                          <p className="mt-0.5 text-[11px] leading-snug text-[var(--ink-3)] line-clamp-2">
                            {flag.description}
                          </p>
                        </div>
                        <Switch
                          checked={Boolean(featureFlags[flag.key])}
                          disabled={flagsSaving}
                          aria-label={`Toggle ${flag.label}`}
                          onCheckedChange={(checked) => toggleFeatureFlag(flag.key, checked)}
                          className="mt-0.5 shrink-0"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        {/* ── Bulk Import ──────────────────────────────── */}
        <section className="mb-10">
          <div className="mb-2 flex items-center justify-between gap-3 border-b-[0.5px] border-[var(--rule)] pb-3">
            <h3 className="clara-section-label">Bulk Import</h3>
            <Switch
              checked={bulkImportsOn}
              disabled={flagsSaving}
              aria-label="Toggle bulk import for this tenant"
              onCheckedChange={(checked) =>
                toggleFeatureFlag('bulk_imports', checked)
              }
              className="shrink-0"
            />
          </div>
          <p className="mb-5 text-[11px] leading-snug text-[var(--ink-3)]">
            Master switch for CSV/Excel bulk upload. Turn it on, then choose which
            claim types are importable - only enabled types appear as upload
            targets for this tenant.
          </p>

          {claimImportLoading ? (
            <p className="py-4 text-[12px] text-[var(--ink-3)]">
              Loading claim types…
            </p>
          ) : claimImportTypes.length === 0 ? (
            <p className="py-4 text-[12px] text-[var(--ink-3)]">
              This tenant has no claim types yet.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {claimImportTypes.map((t) => (
                <div
                  key={t.uuid}
                  className={cn(
                    'flex items-start justify-between gap-3 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] px-4 py-3',
                    !bulkImportsOn && 'opacity-60',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] text-[var(--ink)]">{t.name}</p>
                    <p className="mt-0.5 text-[11px] leading-snug text-[var(--ink-3)]">
                      {t.key}
                    </p>
                  </div>
                  <Switch
                    checked={t.bulk_import_enabled}
                    disabled={!bulkImportsOn || claimImportSavingUuid === t.uuid}
                    aria-label={`Toggle bulk import for ${t.name}`}
                    onCheckedChange={(checked) => toggleClaimImport(t.uuid, checked)}
                    className="mt-0.5 shrink-0"
                  />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Platform API Keys ────────────────────────── */}
        <StaffApiKeysSection
          tenantUuid={uuid ?? ''}
          organisations={organisations.map((o) => ({
            uuid: o.uuid,
            name: o.name,
          }))}
        />

        {/* ── SSO Configuration ────────────────────────── */}
        <section className="mb-10">
          <div className="flex items-center justify-between border-b-[0.5px] border-[var(--rule)] pb-3 mb-5">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-[var(--green)]" />
              <h3 className="clara-section-label">SAML SSO</h3>
            </div>
            {!ssoLoading && !ssoEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSsoEditing(true)}
                className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
              >
                <Pencil className="mr-1 h-3 w-3" />
                {ssoProvider ? 'Edit' : 'Configure'}
              </Button>
            )}
          </div>

          {ssoLoading ? (
            <p className="text-[12px] text-[var(--ink-3)]">Loading SSO configuration...</p>
          ) : ssoEditing ? (
            <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="clara-label block mb-1.5">Display Name</Label>
                  <Input
                    value={ssoForm.display_name}
                    onChange={(e) => setSsoForm((f) => ({ ...f, display_name: e.target.value }))}
                    placeholder="e.g. Microsoft Entra ID"
                    className="rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[12px]"
                  />
                </div>
                <div>
                  <Label className="clara-label block mb-1.5">Entity ID / Issuer</Label>
                  <Input
                    value={ssoForm.entity_id}
                    onChange={(e) => setSsoForm((f) => ({ ...f, entity_id: e.target.value }))}
                    placeholder="https://sts.windows.net/..."
                    className="rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[12px]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="clara-label block mb-1.5">SSO URL</Label>
                  <Input
                    value={ssoForm.sso_url}
                    onChange={(e) => setSsoForm((f) => ({ ...f, sso_url: e.target.value }))}
                    placeholder="https://login.microsoftonline.com/.../saml2"
                    className="rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[12px]"
                  />
                </div>
                <div>
                  <Label className="clara-label block mb-1.5">SLO URL (optional)</Label>
                  <Input
                    value={ssoForm.slo_url}
                    onChange={(e) => setSsoForm((f) => ({ ...f, slo_url: e.target.value }))}
                    placeholder="Single Logout URL"
                    className="rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[12px]"
                  />
                </div>
              </div>
              <div>
                <Label className="clara-label block mb-1.5">SP Entity ID (advanced)</Label>
                <Input
                  value={ssoForm.sp_entity_id}
                  onChange={(e) => setSsoForm((f) => ({ ...f, sp_entity_id: e.target.value }))}
                  placeholder="urn:iwarranty:saml:<slug> (leave blank to use the default)"
                  className="rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[12px]"
                />
                <p className="mt-1.5 text-[11px] text-[var(--ink-3)]">
                  Leave blank unless this tenant registers a separate IdP app per environment within one directory - each app needs a unique SP EntityID.
                </p>
              </div>
              <div>
                <Label className="clara-label block mb-1.5">X.509 Certificate</Label>
                <textarea
                  value={ssoForm.x509_certificate}
                  onChange={(e) => setSsoForm((f) => ({ ...f, x509_certificate: e.target.value }))}
                  rows={4}
                  placeholder="Paste the IdP signing certificate (PEM-encoded, without BEGIN/END markers)"
                  className="w-full rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2 text-[11px] font-mono leading-relaxed resize-y"
                />
              </div>
              <div className="flex items-center justify-between pt-2 border-t-[0.5px] border-t-[var(--rule)]">
                <div className="flex items-center gap-4">
                  <Button
                    onClick={handleSSOSave}
                    disabled={ssoSaving || !ssoForm.display_name || !ssoForm.entity_id || !ssoForm.sso_url}
                    className="rounded-[4px] bg-[var(--green)] px-4 py-1.5 text-[10px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-50"
                  >
                    <Save className="mr-1 h-3 w-3" />
                    {ssoSaving ? 'Saving...' : ssoProvider ? 'Update' : 'Create'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setSsoEditing(false)}
                    className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
                  >
                    <X className="mr-1 h-3 w-3" /> Cancel
                  </Button>
                </div>
                {ssoProvider && (
                  <Button
                    variant="ghost"
                    onClick={() => setSsoConfirmOpen(true)}
                    className="text-[10px] uppercase tracking-[0.1em] text-[var(--status-error)]"
                  >
                    Remove SSO
                  </Button>
                )}
              </div>
            </div>
          ) : ssoProvider ? (
            <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-[14px] text-[var(--ink)]">{ssoProvider.display_name}</span>
                  <Badge variant={ssoProvider.is_enabled ? 'default' : 'secondary'}>
                    {ssoProvider.is_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                <div>
                  <p className="clara-label mb-0.5">Entity ID</p>
                  <p className="text-[12px] font-mono text-[var(--ink-2)] break-all">{ssoProvider.entity_id}</p>
                </div>
                <div>
                  <p className="clara-label mb-0.5">SSO URL</p>
                  <p className="text-[12px] font-mono text-[var(--ink-2)] break-all">{ssoProvider.sso_url}</p>
                </div>
                {ssoProvider.slo_url && (
                  <div>
                    <p className="clara-label mb-0.5">SLO URL</p>
                    <p className="text-[12px] font-mono text-[var(--ink-2)] break-all">{ssoProvider.slo_url}</p>
                  </div>
                )}
                <div>
                  <p className="clara-label mb-0.5">SP Metadata URL</p>
                  <a
                    href={`/accounts/saml/${tenant.slug}/metadata/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[12px] text-[var(--green)] underline"
                  >
                    /accounts/saml/{tenant.slug}/metadata/
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-[8px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] py-8 text-center">
              <ShieldCheck className="mx-auto h-6 w-6 text-[var(--ink-3)] mb-2" />
              <p className="text-[12px] text-[var(--ink-3)]">No SSO configured for this tenant</p>
              <Button
                variant="ghost"
                onClick={() => setSsoEditing(true)}
                className="mt-2 text-[10px] uppercase tracking-[0.1em] text-[var(--green)]"
              >
                Configure SSO
              </Button>
            </div>
          )}
        </section>

        {/* ── Two-column: Orgs + Activity/Members ─────── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Organisations table - 3 cols */}
          <section className="lg:col-span-3">
            <h3
              className="clara-section-label mb-5 border-b-[0.5px] border-[var(--rule)] pb-3"
            >
              Organisations
            </h3>
            <DataTable<TenantDetail['organisations'][number]>
              tableId="staff-tenant-organisations"
              columns={orgColumns}
              data={organisations}
              emptyMessage="No organisations yet"
            />
          </section>

          {/* Right column - Activity + Members */}
          <section className="lg:col-span-2 flex flex-col gap-8">
            {/* Activity feed */}
            <div>
              <h3
                className="clara-section-label mb-5 border-b-[0.5px] border-[var(--rule)] pb-3"
              >
                <Activity className="mr-2 inline-block h-3.5 w-3.5 -mt-0.5 text-[var(--ink-3)]" />
                Recent Activity
              </h3>
              <div className="flex flex-col gap-1">
                {(activityExpanded ? recent_activity : recent_activity.slice(0, 5)).map((entry) => {
                  const colors = actionColor(entry.action)
                  return (
                    <div
                      key={entry.id}
                      className="flex items-start gap-3 px-3 py-2.5 rounded-[8px] transition-colors duration-100 hover:bg-[var(--bg-subtle)]"
                    >
                      <div
                        className={cn(
                          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md',
                          colors
                        )}
                      >
                        <ChevronRight className="h-3 w-3" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[12px] leading-snug text-[var(--ink)]">
                          {entry.description || entry.action.replace(/_/g, ' ')}
                        </p>
                        <p className="mt-0.5 text-[10px] text-[var(--ink-3)]">
                          {timeAgo(entry.timestamp, formatDate)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                {recent_activity.length === 0 && (
                  <p className="px-3 py-4 text-[12px] text-[var(--ink-3)]">
                    No activity recorded for this tenant
                  </p>
                )}
              </div>
              {recent_activity.length > 5 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActivityExpanded((v) => !v)}
                  className="mt-2 w-full text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)]"
                >
                  {activityExpanded ? 'Show less' : `Show all (${recent_activity.length})`}
                </Button>
              )}
            </div>

          </section>
        </div>

        {/* ── Members (full-width table) ───────────────── */}
        <section className="mt-10">
          <div className="mb-5 flex items-end justify-between border-b-[0.5px] border-[var(--rule)] pb-3">
            <h3 className="clara-section-label flex items-center">
              <Users className="mr-2 inline-block h-3.5 w-3.5 -mt-0.5 text-[var(--ink-3)]" />
              Members
            </h3>
            {memberTotal > 0 && (
              <p className="text-[11px] text-[var(--ink-3)]">
                {memberTotal} total
              </p>
            )}
          </div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {(['all', 'tenant', 'partner'] as const).map((scope) => {
                const active = memberScope === scope
                const label = scope === 'all' ? 'All' : scope === 'tenant' ? "Tenant's own" : 'Partner network'
                return (
                  <Button
                    key={scope}
                    variant={active ? 'default' : 'outline'}
                    size="xs"
                    onClick={() => setMemberScope(scope)}
                    className={cn(
                      'rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.08em]',
                      !active && 'border-[0.5px] border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]',
                    )}
                  >
                    {label}
                  </Button>
                )
              })}
            </div>
            <div className="w-72 max-w-full">
              <SearchInput
                placeholder="Search by name, email, or organisation..."
                value={memberSearch}
                onChange={setMemberSearch}
                iconSize={12}
                className="w-full rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] py-1.5 pr-3 text-[12px] text-[var(--ink)]"
              />
            </div>
          </div>
          <DataTable<MemberRow>
            tableId="staff-tenant-members"
            columns={memberColumns}
            data={members}
            isLoading={membersLoading}
            emptyMessage={memberSearch.length >= 2 ? 'No members match this search' : 'No members yet'}
            page={memberPage}
            pageSize={memberPageSize}
            totalCount={memberTotal}
            onPageChange={setMemberPage}
            onPageSizeChange={() => {}}
            rowKey={(row) => `${row.user_uuid}-${row.organisation_uuid}`}
          />
        </section>

        {/* ── Partner Network (hierarchy summary + paginated list) ── */}
        {partnership_count > 0 && (
          <section className="mt-10">
            <div className="mb-5 flex items-end justify-between border-b-[0.5px] border-[var(--rule)] pb-3">
              <h3 className="clara-section-label flex items-center">
                <Handshake className="mr-2 inline-block h-3.5 w-3.5 -mt-0.5 text-[var(--ink-3)]" />
                Partner Network
              </h3>
              <p className="text-[11px] text-[var(--ink-3)]">
                {partnership_count} active partnership{partnership_count === 1 ? '' : 's'}
              </p>
            </div>
            {partner_network && (
              <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="flex items-start gap-3 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)]">
                    <Network className="h-3.5 w-3.5 text-[var(--green)]" />
                  </div>
                  <div>
                    <p className="mb-0.5 text-[22px] leading-none text-[var(--ink)]">
                      {partner_network.main_groups}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">Main Groups</p>
                    <p className="mt-1 text-[10px] leading-snug text-[var(--ink-3)]">
                      {partner_network.dealer_groups} group{partner_network.dealer_groups !== 1 ? 's' : ''} + {partner_network.independent_partners} independent
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
                    <Store className="h-3.5 w-3.5 text-[var(--ink-3)]" />
                  </div>
                  <div>
                    <p className="mb-0.5 text-[22px] leading-none text-[var(--ink)]">
                      {partner_network.dealer_sites}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">Dealer Sites</p>
                    <p className="mt-1 text-[10px] leading-snug text-[var(--ink-3)]">
                      Sites within groups
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
                    <Handshake className="h-3.5 w-3.5 text-[var(--ink-3)]" />
                  </div>
                  <div>
                    <p className="mb-0.5 text-[22px] leading-none text-[var(--ink)]">
                      {partner_network.total_partnerships}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">Total Partnerships</p>
                    <p className="mt-1 text-[10px] leading-snug text-[var(--ink-3)]">
                      All active agreements
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* Search + table */}
            <div className="mb-4 flex items-center justify-end">
              <div className="w-56">
                <SearchInput
                  placeholder="Search partners..."
                  value={partnerSearch}
                  onChange={setPartnerSearch}
                  iconSize={12}
                  className="w-full rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] py-1.5 pr-3 text-[12px] text-[var(--ink)]"
                />
              </div>
            </div>
            <DataTable<PartnerRow>
              tableId="staff-tenant-partners"
              columns={partnerColumns}
              data={partners}
              isLoading={partnersLoading}
              emptyMessage="No partnerships found"
              page={partnerPage}
              pageSize={partnerPageSize}
              totalCount={partnerTotal}
              onPageChange={setPartnerPage}
              onPageSizeChange={() => {}}
              onRowClick={(row) => navigate(`/staff/partners/${row.uuid}`)}
              rowKey={(row) => row.uuid}
            />
          </section>
        )}

        {/* ── Danger Zone ───────────────────────────────────── */}
        <section className="mt-16">
          <h3
            className="clara-section-label mb-5 border-b-[0.5px] border-[var(--status-error-border)] pb-3 text-[var(--status-error)]"
          >
            <AlertTriangle className="mr-2 inline-block h-3.5 w-3.5 -mt-0.5" />
            Danger Zone
          </h3>
          <div className="flex items-start justify-between gap-6 rounded-[8px] border-[0.5px] border-[var(--status-error-border)] bg-[var(--status-error-bg)] p-5">
            <div className="flex-1">
              <p className="mb-1 text-[13px] text-[var(--ink)]">
                Delete all registrations
              </p>
              <p className="text-[12px] leading-[1.5] text-[var(--ink-2)]">
                Permanently removes every Holding (registration) for this
                tenant, plus its Warranties, Claims (with their settlements,
                payments, and attribute values), Inspections, and stored
                attribute values. Workflow instances are not FK-linked and
                will be left as orphans. This action cannot be undone.
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={openBulkDeleteDialog}
              className="shrink-0"
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Delete registrations
            </Button>
          </div>
        </section>
          </TabsContent>
        </Tabs>
      </main>
      <ConfirmDeleteDialog
        open={ssoConfirmOpen}
        onOpenChange={(open) => {
          if (!ssoDeleting) setSsoConfirmOpen(open)
        }}
        title="Remove SSO provider?"
        description={
          <>
            This permanently disables single sign-on for{' '}
            <strong>{ssoProvider?.display_name || 'this tenant'}</strong>.
            Existing users will need to authenticate with their email and
            password until a new provider is configured.
          </>
        }
        confirmLabel="Remove"
        onConfirm={handleSSODelete}
        isPending={ssoDeleting}
      />

      <BulkDeleteRegistrationsDialog
        open={bulkDeleteOpen}
        onOpenChange={(open) => {
          if (!bulkDeleteApplying) setBulkDeleteOpen(open)
        }}
        tenantSlug={tenant.slug}
        previewLoading={bulkDeletePreviewLoading}
        preview={bulkDeletePreview}
        isProd={bulkDeleteIsProd}
        prodPhrase={bulkDeleteProdPhrase}
        confirmInput={bulkDeleteConfirmInput}
        onConfirmInputChange={setBulkDeleteConfirmInput}
        prodInput={bulkDeleteProdInput}
        onProdInputChange={setBulkDeleteProdInput}
        alsoDeleteTransfers={bulkDeleteAlsoTransfers}
        onAlsoDeleteTransfersChange={setBulkDeleteAlsoTransfers}
        applying={bulkDeleteApplying}
        onApply={handleBulkDeleteApply}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bulk-delete registrations dialog
// ---------------------------------------------------------------------------

interface BulkDeletePreviewShape {
  holdings: number
  registration_field_values: number
  warranties: number
  claims: number
  inspections: number
  transfer_registrations: number
  transfers: number
}

interface BulkDeleteRegistrationsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tenantSlug: string
  previewLoading: boolean
  preview: BulkDeletePreviewShape | null
  isProd: boolean
  prodPhrase: string | null
  confirmInput: string
  onConfirmInputChange: (value: string) => void
  prodInput: string
  onProdInputChange: (value: string) => void
  alsoDeleteTransfers: boolean
  onAlsoDeleteTransfersChange: (value: boolean) => void
  applying: boolean
  onApply: () => void
}

function BulkDeleteRegistrationsDialog({
  open,
  onOpenChange,
  tenantSlug,
  previewLoading,
  preview,
  isProd,
  prodPhrase,
  confirmInput,
  onConfirmInputChange,
  prodInput,
  onProdInputChange,
  alsoDeleteTransfers,
  onAlsoDeleteTransfersChange,
  applying,
  onApply,
}: BulkDeleteRegistrationsDialogProps) {
  const hasRows = (preview?.holdings ?? 0) > 0
  const transfersBlocking = (preview?.transfer_registrations ?? 0) > 0
  const confirmMatches = confirmInput === 'delete'
  const prodPhraseMatches = !isProd || (prodPhrase !== null && prodInput === prodPhrase)
  const transferGateOk = !transfersBlocking || alsoDeleteTransfers
  const canApply =
    hasRows &&
    !previewLoading &&
    !applying &&
    confirmMatches &&
    prodPhraseMatches &&
    transferGateOk

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--status-error)]">
            <AlertTriangle className="h-4 w-4" />
            Delete all registrations for {tenantSlug}?
          </DialogTitle>
          <DialogDescription className="text-[13px] leading-[1.5] text-[var(--ink-2)]">
            This will permanently delete every registration for this tenant
            along with its warranties, claims, inspections, and related data.
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {previewLoading ? (
          <p className="py-6 text-center text-[12px] text-[var(--ink-3)]">
            Loading preview…
          </p>
        ) : preview === null ? (
          <p className="py-6 text-center text-[12px] text-[var(--ink-3)]">
            Preview unavailable.
          </p>
        ) : !hasRows ? (
          <p className="py-6 text-center text-[12px] text-[var(--ink-3)]">
            This tenant has no registrations. Nothing to delete.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
              <p className="mb-2 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Rows affected
              </p>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
                <PreviewRow label="Registrations" value={preview.holdings} />
                <PreviewRow
                  label="Attribute values"
                  value={preview.registration_field_values}
                />
                <PreviewRow label="Warranties" value={preview.warranties} />
                <PreviewRow label="Claims" value={preview.claims} />
                <PreviewRow label="Inspections" value={preview.inspections} />
                <PreviewRow
                  label="Transfer registrations"
                  value={preview.transfer_registrations}
                />
                <PreviewRow label="Transfers" value={preview.transfers} />
              </dl>
            </div>

            {transfersBlocking ? (
              <label className="flex cursor-pointer items-start gap-2.5 rounded-[6px] border-[0.5px] border-[var(--status-warn-border)] bg-[var(--status-warn-bg)] p-3">
                <Checkbox
                  checked={alsoDeleteTransfers}
                  onCheckedChange={(checked) =>
                    onAlsoDeleteTransfersChange(Boolean(checked))
                  }
                  className="mt-0.5"
                />
                <div className="flex-1 text-[12px] leading-[1.45]">
                  <p className="text-[var(--ink)]">
                    Also delete blocking transfers
                  </p>
                  <p className="text-[var(--ink-3)]">
                    {preview.transfer_registrations} transfer registration row(s)
                    reference these holdings with on_delete=PROTECT. Required
                    to proceed.
                  </p>
                </div>
              </label>
            ) : null}

            <div>
              <Label className="mb-1.5 block text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Type <span className="font-mono text-[var(--ink)]">delete</span> to confirm
              </Label>
              <Input
                value={confirmInput}
                onChange={(e) => onConfirmInputChange(e.target.value)}
                placeholder="delete"
                autoComplete="off"
                disabled={applying}
              />
            </div>

            {isProd && prodPhrase ? (
              <div>
                <Label className="mb-1.5 block text-[11px] uppercase tracking-[0.1em] text-[var(--status-error)]">
                  Production environment - type the phrase below verbatim
                </Label>
                <p className="mb-2 rounded-[4px] bg-[var(--bg-subtle)] px-2 py-1.5 font-mono text-[11px] leading-[1.4] text-[var(--ink)]">
                  {prodPhrase}
                </p>
                <Input
                  value={prodInput}
                  onChange={(e) => onProdInputChange(e.target.value)}
                  placeholder="Type the phrase exactly"
                  autoComplete="off"
                  disabled={applying}
                />
                <p className="mt-1.5 text-[11px] text-[var(--ink-3)]">
                  Requires a superuser account.
                </p>
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={applying}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!canApply}
            onClick={onApply}
          >
            {applying ? 'Deleting...' : 'Delete permanently'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PreviewRow({ label, value }: { label: string; value: number }) {
  return (
    <>
      <dt className="text-[var(--ink-3)]">{label}</dt>
      <dd className="text-right font-mono text-[var(--ink)]">
        {value.toLocaleString()}
      </dd>
    </>
  )
}
